import { ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import {
  faArrowRight,
  faCalendar,
  faChalkboardUser,
  faClock,
  faIndent,
  faLocationDot,
  faStairs,
  faUser,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { FormatSettings } from '@progress/kendo-angular-dateinputs';
import { DialogService } from '@progress/kendo-angular-dialog';
import { NotificationService } from '@progress/kendo-angular-notification';
import moment from 'moment';
import { Observable, combineLatest, of, takeUntil } from 'rxjs';
import { EvaluationsTabComponent } from 'src/app/modules/dashboard/components/evaluations-tab/evaluations-tab.component';
import {
  DashboardEvalResponse,
  DashboardResponse,
  EvaluationList,
} from 'src/app/modules/dashboard/interface/dashboard-response';
import { DashboardService } from 'src/app/modules/dashboard/services/dashboard.service';
import { iepSections } from 'src/app/modules/iep/iep-document/constants/iep-sections';
import { IEPService } from 'src/app/modules/iep/iep-document/services/iep.services';
import { SCHEDULE_MEETING_CONSTANTS } from 'src/app/modules/student-info/schedule-meeting/schedule-meeting-constants';
import { LoadingService } from 'src/app/services/loading.service';
import { AuthStore } from 'src/app/stores/auth.store';
import { ReferenceStore } from 'src/app/stores/reference.store';
import {
  PENDING_SCHEDULING,
  SCHEDULED, ZERO_MONTH_DIFFERENCE
} from '../../constants/assessment-scheduling';
import {
  ADDITIONAL_MINUTE,
  HOURS,
  MINUTES,
  MS,
  SECONDS,
} from '../../constants/time-constants';
import { MeetingSettingType } from '../../enums/meeting-setting-type';
import { MeetingType } from '../../enums/meeting-type';
import { IReferenceData } from '../../interfaces/form/reference-data.model';
import { ISedmsFormModel } from '../../interfaces/form/sedms-form-model';
import { AssessmentScheduleMeetingFormModel } from '../../models/assessment-schedule-meeting/assessment-schedule-meeting-formmodel';
import { AssessmentScheduleMeetingRefData } from '../../models/assessment-schedule-meeting/assessment-schedule-meeting-reference.model';
import { AssessmentScheduleMeetingRequestModel } from '../../models/assessment-schedule-meeting/assessment-schedule-meeting-request-model';
import { AssessmentScheduleMeetingSubmitModel } from '../../models/assessment-schedule-meeting/assessment-schedule-meeting-submit-model';
import { ScheduleMeetingRequestModel } from '../../models/schedule-meeting-models/schedule-meeting-request-model';
import {
  Assessment,
  SubmitScheduleMeetingModel,
} from '../../models/schedule-meeting-models/submit-schedule-meeting-model';
import { User } from '../../models/user';

import { EvaluationService } from 'src/app/modules/evaluations/services/evaluation.service';
import { IMeetingDetails } from '../../interfaces/meeting-details';
import { AbstractForm } from '../abstract-form';
import { ApiResponse } from '../../interfaces/api-response';
import { ISedmsFormOutput } from '../../interfaces/form/sedms-form-output.model';

@Component({
  selector: 'app-assessment-schedule-meeting',
  templateUrl: './assessment-schedule-meeting.component.html',
  styleUrls: ['./assessment-schedule-meeting.component.scss'],
  standalone: false,
})
export class AssessmentScheduleMeetingComponent extends AbstractForm<
  AssessmentScheduleMeetingRequestModel,
  AssessmentScheduleMeetingSubmitModel,
  Record<string, IReferenceData[]>
> {


  override canSubmit: boolean;
  @Input() evaluationDetails: EvaluationList;
  @Input() studentDetails: DashboardResponse;
  @Input() evaluationSk: string;
  @Input() actionText: string;
  @Input() referralSk: string;
  @Input() meetingSk: string;
  @Input() meetingType: MeetingType;
  @Input() isReSchedule: boolean = false;
  @Input() assessments: EvaluationList[] = [];
  @Input() nycIds: string;
  @Input() assessmentSk: string;
  @Output() closeDialog: EventEmitter<void> = new EventEmitter<void>();
  meetingSettingTypeENUM: typeof MeetingSettingType = MeetingSettingType;
  formModel: ISedmsFormModel = AssessmentScheduleMeetingFormModel;
  form: FormGroup;
  loadSpinner: boolean;
  showRescheduleMeeting: boolean;
  confirmationDialog: boolean = false;
  timePickerMin: Date = SCHEDULE_MEETING_CONSTANTS.timePickerDefaultMin;
  timePickerMax: Date = SCHEDULE_MEETING_CONSTANTS.timePickerDefaultMax;
  timePickerValue: Date = SCHEDULE_MEETING_CONSTANTS.timePickerDefaultValue;
  scheduleMeetingList: IMeetingDetails[] = [];
  meetingTypeEnum: typeof MeetingType = MeetingType;
  public format: FormatSettings = {
    displayFormat: 'MM/dd/yyyy',
    inputFormat: 'MM/dd/yyyy',
  };
  currentDate: Date = new Date(
    new Date().setHours(HOURS, MINUTES, SECONDS, MS)
  );
  userDetails: User;
  maxMeetingDate: Date;
  minMeetingDate: Date | null = null;
  pwnNoticeDate: Date;
  meetingStartTime: string;
  meetingEndTime: string;
  readonly indexNumber: number = 1;
  endTimeMin: Date | null = null;
  constructor(
    library: FaIconLibrary,
    route: ActivatedRoute,
    cdRef: ChangeDetectorRef,
    formBuilder: FormBuilder,
    dialogService: DialogService,
    public activatedRoute: ActivatedRoute,
    notificationService: NotificationService,
    private referenceStore: ReferenceStore,
    private evaluationService: EvaluationService<ISedmsFormOutput>,
    private dashboardService: DashboardService,
    private authStore: AuthStore,
    private evalTabComponent: EvaluationsTabComponent,
    private iepService: IEPService<SubmitScheduleMeetingModel>,
    loadingService: LoadingService) {
    super(loadingService, cdRef, route, formBuilder, dialogService, notificationService);
    library.addIcons(
      faCalendar,
      faStairs,
      faArrowRight,
      faClock,
      faIndent,
      faUsers,
      faLocationDot,
      faUser,
      faChalkboardUser
    );
    this.loadSpinner = true;
    this.authStore.UserInfo$.pipe(takeUntil(this.componentDestroyed)).subscribe(
      (userInfo: User) => {
        this.userDetails = userInfo;
      }
    );
    this.maxMeetingDate = moment().add(this.indexNumber, 'year').endOf('day').toDate(); //Maximum Date must be <= 1 year after current date
  }

  initEntity(
    entityData: any,
    referenceData: AssessmentScheduleMeetingRefData
  ): AssessmentScheduleMeetingRequestModel {
    return new AssessmentScheduleMeetingRequestModel(this.evaluationDetails);
  }

  override initReferenceData([extReq, assessmentRejection]: [
    IReferenceData[],
    IReferenceData[]
  ]): Record<string, IReferenceData[]> {
    return { extReq, assessmentRejection };
  }

  get entity$(): Observable<any> {
    return of({});
  }

  get referenceData$(): Observable<IReferenceData[][]> {
    return combineLatest([
      this.referenceStore.extReq$,
      this.referenceStore.assessmentRejection$,
    ]);
  }


  initWatches(): void {
    //
  }

  public override otherInitalizations(): void {
    this.getDropdownDetails();
    this.patchMeetingDetails();
    this.form.patchValue({
      evaluatorName: this.evaluationDetails.evaluatorName,
    });
    this.form.get('evaluatorName').disable();
    this.setMinMeetingDate();

    const start: Date = this.form.get('meetingStartTime')?.value;
    this.endTimeMin = this.getMeetingEndTimeValue(start);
    this.form.get('meetingStartTime')?.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((value: Date) => {
        this.endTimeMin = this.getMeetingEndTimeValue(value);
      });
  }

  setMinMeetingDate(): void {
    this.evaluationService
      .getNoticeDate(this.referralSk)
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((response: { noticeDate: Date }) => {
        this.minMeetingDate = response.noticeDate
          ? moment(response.noticeDate).startOf('day').toDate()
          : null; //Minimum Date must be >= Notice Date of PWN (Referrals)
        this.pwnNoticeDate = response.noticeDate;
      });
  }

  getEvalsData(): void {
    this.dashboardService
      .getEvalsData(this.studentDetails.referralSK)
      .subscribe((res: DashboardEvalResponse) => {
        this.assessments = this.isReSchedule
          ? res.evaluationList.filter(
            (a: EvaluationList) =>
              a.evaluationStatus === SCHEDULED && a.evaluatorName !== ' '
          )
          : res.evaluationList.filter(
            (a: EvaluationList) =>
              a.evaluationStatus === PENDING_SCHEDULING &&
              a.evaluatorName !== ' '
          );
      });
  }
  // getTreeStructureData(filteredAttendees: any[]): any[] {
  //   const ID_OFFSET : number = 1;
  //   const attendees: Record<string, any[]> = filteredAttendees.reduce(function (r: Record<string, any[]>, a: any, i: number) {
  //     r[a.title] = r[a.title] || [];
  //     a.text =
  //       a.firstName + ' ' + a.lastName + (a.email ? ' , ' + a.email : '');
  //     a.id = i + ID_OFFSET;
  //     r[a.title].push(a);
  //     return r;
  //   }, Object.create(null) as Record<string, any[]>);

  //   const dataAttendees: any[] = [];
  //   let idcount : number= 1000;
  //   for (const title in attendees) {
  //     dataAttendees.push({
  //       text: title,
  //       id: idcount,
  //       children: attendees[title],
  //     });
  //     idcount++;
  //   }
  //   return dataAttendees;
  // }

  getDropdownDetails(): void {
    this.studentDetails.age = this.calculateAge(this.studentDetails.dob);
    this.loadSpinner = false;
  }

  calculateAge(dob: Date | string): number {
    const birthDate: Date = new Date(dob);
    const today: Date = new Date();
    let age: number = today.getFullYear() - birthDate.getFullYear();
    const monthDifference: number = today.getMonth() - birthDate.getMonth();
    if (
      monthDifference < ZERO_MONTH_DIFFERENCE ||
      (monthDifference === ZERO_MONTH_DIFFERENCE &&
        today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  }

  postCancelConfirmation(): void {
    //
  }

  postSubmitConfirmation(): void {
    this.evalTabComponent.ngOnInit();
    this.closeDialog.emit();
  }

  // getAssessmets() {
  //   const assessments = [];
  //   this.form?.value?.assessmentsList?.forEach((assessment) => {
  //     assessments.push({ assessmentSk: assessment });
  //   });
  //   return assessments;
  // }

  patchMeetingDetails(): void {
    if (this.meetingSk) {
      this.iepService
        .getMeetingDetails(this.meetingSk, iepSections.meeting)
        .pipe(takeUntil(this.componentDestroyed))
        .subscribe((data: IMeetingDetails) => {
          this.scheduleMeetingList = [data];

          const fromJsonData = data?.formJson && JSON.parse(data?.formJson)
          if (fromJsonData) {
            this.meetingStartTime = fromJsonData.meetingStartTime;
            this.meetingEndTime = fromJsonData.meetingEndTime;

            const formModel: ScheduleMeetingRequestModel =
              new ScheduleMeetingRequestModel(fromJsonData);
            this.form.patchValue(formModel);
          }
        });
    }
  }

  saveDetails(): Observable<ApiResponse<IMeetingDetails>> {
    const assessments: Assessment[] = [];
    assessments.push({ assessmentSk: this.evaluationDetails?.evaluationSk });
    const scheduleMeetingData: SubmitScheduleMeetingModel =
      new SubmitScheduleMeetingModel({
        ...this.form.value,
        noticeDate: this.pwnNoticeDate,
      });
    scheduleMeetingData.studentSk = this.studentDetails.studentSK;
    scheduleMeetingData.parentSk = this.evaluationDetails?.evaluationSk;
    scheduleMeetingData.assessments = assessments;
    scheduleMeetingData.nycId = this.nycIds;
    scheduleMeetingData.meetingType = 5 as any;
    scheduleMeetingData.personSk = this.userDetails.personSk; // this.studentDetails.personSK;
    scheduleMeetingData.evaluationSk = this.evaluationDetails?.evaluationSk;
    scheduleMeetingData.pwnNoticeDate = this.pwnNoticeDate;
    if (
      this.actionText === 'Schedule Evaluation Meeting' ||
      this.actionText === 'Reschedule Evaluation Meeting'
    ) {
      scheduleMeetingData.subMeetingType =
        this.meetingSettingTypeENUM.Assessment;
    }
    if (this.meetingSk) {
      scheduleMeetingData.meetingSk = this.meetingSk;
      return this.iepService.updateMeeting(
        scheduleMeetingData,
        iepSections.meeting
      );
    } else {
      return this.iepService.submitMeeting(
        scheduleMeetingData,
        iepSections.meeting
      );
    }
  }

  get canSaveDraft(): boolean {
    return !this.form.valid;
  }

  openConfirmationDialog(): void {
    if (!this.viewMode) this.confirmationDialog = true;
    else this.onCancelSEM();
  }

  closeConfirmationDialog(): void {
    this.confirmationDialog = false;
  }

  onCancelSEM(): void {
    this.confirmationDialog = false;
    this.closeDialog.emit();
  }

  // public tagMapper(tags: any[]): any[] {
  //   return tags.length < TAG_MAPPER_LENGTH ? tags : [tags];
  // }

  getMeetingEndTimeValue(start: Date | null): Date | null {
    if (start instanceof Date && !isNaN(start.getTime())) {
      const minEnd = new Date(start);
      minEnd.setMinutes(minEnd.getMinutes() + ADDITIONAL_MINUTE);
      return minEnd;
    }
    return null;
  }

  get formatMeetingTime(): string {
  const start = this.meetingStartTime ? moment(this.meetingStartTime, 'MM/DD/YYYY hh:mm A').format('MM/DD/YYYY, hh:mm A') : '';
  const end = this.meetingEndTime ? moment(this.meetingEndTime).format('hh:mm A') : '';

  if(start || end ) return  `${start} to ${end}`;
  return '';
}


}
